import React, { useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  SectionHeader,
  Typography,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';
import { useSpecialConsiderationReport } from '../../logic/useSpecialConsiderationReport';
import { usePostSpecialConsiderationReport } from '../../../../api-refactored/reports/usePostSpecialConsiderationReport';
import { useGetCheckAuthorityExist } from '../../../../api-refactored/user/useGetCheckAuthorityExist';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  singleFormElementContainer: {
    width: '50%',
  },
  errorContainer: {
    display: 'flex',
  },
  errMsg: {
    marginTop: '0 !important',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: '120px',
  },
  menuItemsContainer: {
    width: '65%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    position: 'relative',
    transform: 'none!important',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 !important',
  },
  fromControlContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  radioContainer: {},
}));

const SpecialConsiderationReportDetails = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [generateTouched, setGenerateTouched] = useState(false);
  const {
    form,
    submitGenerate,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  } = useSpecialConsiderationReport();
  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  usePostSpecialConsiderationReport();

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    if ((watchStartDate && generateTouched) || (watchEndDate && generateTouched)) {
      trigger('endDate');
      trigger('startDate');
      trigger('custom');
    }
  }, [watchStartDate, watchEndDate, trigger, generateTouched]);

  const checkStartDate = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'Application End Date should be later than Application Start Date',
        });
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('12.0')) {
        form.setError('root.custom', {
          type: 'manual',
          message: 'Date range should not be more than 12 months.',
        });
        return false;
      }
    }
    return true;
  };

  const checkDateRangeHandler = (formValue: any) => {
    const checkDate = checkStartDate();
    if (!checkDate) {
      return;
    }
    submitGenerate({ ...formValue });
  };

  const onGenerateClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setGenerateTouched(true);
    } else {
      setGenerateTouched(false);
    }
  };

  useGetCheckAuthorityExist({
    authorityName: AUTHORITY_NAMES.CP_ADMIN_SPECIAL_CONSIDERATION_REPORT_VIEW,
  });

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('admin->>SpecialConsiderationReport.ReportDetails')} />
      <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
        <Box className={classes.formElementContainer}>
          <ControlledDatePicker
            label={t('admin->>AccessArrangementReport.AccessArrangementStartDate')}
            onChange={(date) => {
              setStartDate(date || '');
              setValue('startDate', date || '');
            }}
            control={control}
            name="startDate"
            disableFuture
            error={!!errors.startDate}
            helperText={errors?.startDate?.message?.toString()}
            inputVariant="filled"
            autoOk
            isRequired
            maxDate={dayjs()}
          />
        </Box>

        <Box className={classes.formElementContainer}>
          <ControlledDatePicker
            label={t('admin->>AccessArrangementReport.AccessArrangementEndDate')}
            onChange={(date) => {
              setEndDate(date || '');
              setValue('endDate', date || '');
            }}
            control={control}
            name="endDate"
            maxDate={dayjs()}
            disableFuture
            error={!!errors.endDate}
            helperText={errors?.endDate?.message?.toString()}
            inputVariant="filled"
            autoOk
            isRequired
          />
        </Box>

        <Box className={classes.errorContainer}>
          <Typography className={classes.label} variant="subtitle2">
            {t('admin->>SpecialConsiderationReport.DateRangeMonths', { noOfMonths: 12 })}
          </Typography>
        </Box>
        {errors?.root?.custom?.message !== '' && (
          <Box
            className={[classes.formElementContainer, classes.singleFormElementContainer].join(' ')}
          >
            <ErrorInfo label={errors?.root?.custom?.message?.toString()} />
          </Box>
        )}

        <Box className={classes.submitButtonContainer}>
          <ApiButton
            id="button-generate-report"
            status={process}
            type="submit"
            onClick={onGenerateClickedHandler}
          >
            {t('admin->>SpecialConsiderationReport.GenerateReport')}
          </ApiButton>
        </Box>
      </form>
      {/* <LoadingComponent isOpaque /> */}
      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogState}
        onDialogClose={onDialogClose}
      />
    </ContentTemplate>
  );
};

export default SpecialConsiderationReportDetails;
